import { FunctionComponent, RefObject, useRef, useState } from 'react'
import moment from 'moment-timezone'
import { ArrowBackIosNew, ArrowForwardIos, Print, Settings } from '@mui/icons-material'
import styles from './mealHandler.module.scss'
import { Daycare } from '@/types/Daycare'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper'
import Loading from '@/components/Loading/Loading'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector'
import { HierarchicalOption, SelectOptionsDeprecated } from '@/types/Select'
import { useAuth } from '@/modules/context/AuthContext'
import { MealAccessibilitySection, MealChild, MealChildWithoutMenu, MealType, MealTypeDetail } from '@/types/Meal'
import { CalendarIcon } from '@mui/x-date-pickers'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import DatePickerDialog from '../../planning/Dialog/datePicker/datePickerDialog'
import SelectHierarchical from '@/components/_custom/Selector/selectHierarchical/selectHierarchical'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { useDateNavigation } from '@/hooks/useDateNavigation'
import { useMealData } from '@/hooks/useMealData'
import { format, parse } from 'date-fns'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import Desktop from '@/modules/desktopMobile/desktop.tsx'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import UpdateMealQuantityDialog from './Dialog/UpdateMealQuantityDialog.tsx'
import ExportMealDialog from './Dialog/ExportMealDialog.tsx'
import MealDetailedView from './mealDetailedView.tsx'
import MealChildrenTable from './mealChildrenTable.tsx'
import MealIndicators from './mealIndicators.tsx'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos.ts'

interface MealHandlerProps {
    currentDaycare: Daycare
    defaultDate: string | null
    defaultSections: string | null
}

const MealHandler: FunctionComponent<MealHandlerProps> = ({
    currentDaycare,
    defaultDate,
    defaultSections
}: MealHandlerProps) => {
    const { t } = useTranslation()
    const { user } = useAuth()
    const navigate: NavigateFunction = useNavigate()

    const {
        date,
        handleNextDate,
        handlePreviousDate,
        setDateValues,
        selectedSectionAndSectionGroup,
        setSelectedSectionAndSectionGroup,
        getSectionIdsFromSelection
    } = useDateNavigation({
        daycare: currentDaycare,
        defaultDate,
        onDateChange: (formatedDate: string, sectionIds: number[]): void => {
            fetchMealDetail(formatedDate, currentDaycare?.id, sectionIds)
        }
    })

    const {
        loadingMealDetail,
        isClosingDay,
        mealAccessibilitySections,
        mealDayDetailByType,
        availableMenus,
        availableTextures,
        availableHabits,
        selectedSections,
        selectedMealTypes,
        setSelectedMealTypes,
        availableMealTypes,
        fetchMealDetail,
        refetchMealDetail,
        handleFreezeMealType,
        handleAdjustOrder,
        getOrderAdjustment
    } = useMealData({
        daycareId: currentDaycare?.id,
        date,
        defaultSections,
        selectedSectionAndSectionGroup,
        getSectionIdsFromSelection,
        setSelectedSectionAndSectionGroup
    })

    const [isAddMealDialogOpen, setAddMealDialogOpen] = useState(false)
    const [isDatePickerDialogOpen, setDatePickerDialogOpen] = useState(false)
    const [isExportDialogOpen, setExportDialogOpen] = useState(false)
    const childrenTableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null)

    function handleSelectSection(selected: string[]): void {
        setSelectedSectionAndSectionGroup(selected)
        fetchMealDetail(date, currentDaycare?.id, getSectionIdsFromSelection(selected), selectedMealTypes)
    }

    function handleSelectMealTypes(selected: string[]): void {
        setSelectedMealTypes(selected)
        fetchMealDetail(date, currentDaycare?.id, getSectionIdsFromSelection(selectedSectionAndSectionGroup), selected)
    }

    const mealTypeTranslations: Record<string, string> = {
        [MealType.breakfast]: t('mealTypeBreakfast'),
        [MealType.lunch]: t('mealTypeLunch'),
        [MealType.snack]: t('mealTypeSnack'),
        [MealType.dinner]: t('mealTypeDinner')
    }

    const mealTypeOptions: SelectOptionsDeprecated = [
        {
            options: availableMealTypes.map((type: MealType) => ({
                name: mealTypeTranslations[type] || type,
                value: type
            }))
        }
    ]

    const uniqueSections: { sectionId: number; sectionName: string }[] = mealAccessibilitySections.reduce(
        (acc: { sectionId: number; sectionName: string }[], section: MealAccessibilitySection) => {
            if (!acc.some((s) => s.sectionId === section.sectionId)) {
                acc.push({ sectionId: section.sectionId, sectionName: section.sectionName })
            }
            return acc
        },
        []
    )

    const selectHierarchicalOptions: HierarchicalOption[] = uniqueSections.map(
        (section): HierarchicalOption => ({
            name: section.sectionName,
            value: 'SECTION_' + section.sectionId.toString()
        })
    )

    const { isRpeOrLaep } = useCurrentDaycareInfos()

    const globalChilds: MealChild[] = mealDayDetailByType
        ? [
              ...new Map(
                  mealDayDetailByType.typeDetails
                      .flatMap((td: MealTypeDetail): MealChild[] => td.childs)
                      .map((c: MealChild): [string, MealChild] => [`${c.childId}_${c.start}_${c.end}`, c])
              ).values()
          ]
        : []

    const childsWithoutMenu: MealChildWithoutMenu[] = mealDayDetailByType
        ? [
              ...new Map(
                  mealDayDetailByType.typeDetails
                      .flatMap((td: MealTypeDetail): MealChildWithoutMenu[] => td.childsWithoutMenu)
                      .map((c: MealChildWithoutMenu): [string, MealChildWithoutMenu] => [
                          `${c.childId}_${c.start}_${c.end}`,
                          c
                      ])
              ).values()
          ]
        : []

    const hasDetails: boolean =
        mealDayDetailByType?.typeDetails.some((td: MealTypeDetail): boolean => td.details.length > 0) ?? false

    const canUpdate: boolean =
        !!user?.currentDaycareRights.canWriteMeal && moment(date, 'DD-MM-YYYY').isSameOrAfter(moment(), 'day')

    return mealAccessibilitySections.length > 0 ? (
        <>
            <div className={styles.SelectorContainer}>
                {user && user.daycares.length > 1 && (
                    <div className={styles.SelectorLeft}>
                        <DaycareSelector />
                    </div>
                )}
                <div className={styles.SelectorRight}>
                    <div className={styles.SectionGroupContainer}>
                        {hasDetails && (
                            <div className={styles.Parameters}>
                                <ButtonIcon
                                    icon={<Print />}
                                    onClick={() => setExportDialogOpen(true)}
                                    className={styles.ButtonIcon}
                                />
                            </div>
                        )}
                        <div className={styles.SectionGroup}>
                            <SelectHierarchical
                                label={t('sections')}
                                options={selectHierarchicalOptions}
                                value={selectedSectionAndSectionGroup}
                                onChange={handleSelectSection}
                                fullWidth={true}
                            />
                        </div>
                        {user?.currentDaycareRights.canReadParamsDaycare && user.daycares.length > 1 && (
                            <Desktop>
                                <div className={styles.Parameters}>
                                    <Settings
                                        className={styles.Icon}
                                        onClick={() => navigate(`/daycare?daycare=${user?.currentDaycare?.id}#meal`)}
                                    />
                                </div>
                            </Desktop>
                        )}
                    </div>
                </div>
                {user?.currentDaycareRights.canReadParamsDaycare && user.daycares.length <= 1 && (
                    <Desktop>
                        <div className={styles.Parameters}>
                            <Settings
                                className={styles.Icon}
                                onClick={() => navigate(`/daycare?daycare=${user?.currentDaycare?.id}#meal`)}
                            />
                        </div>
                    </Desktop>
                )}
            </div>
            <div className={styles.MealContainer}>
                <div className={styles.Header}>
                    <ArrowBackIosNew className={styles.ArrowIcon} onClick={handlePreviousDate} />
                    <div className={styles.TitleAndCalendar}>
                        <p className={styles.DateTitle}>
                            {format(parse(date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                locale: getLocalFromLanguage()
                            })}
                        </p>
                        <ButtonIcon
                            icon={<CalendarIcon />}
                            onClick={() => setDatePickerDialogOpen(true)}
                            className={styles.CalendarIcon}
                        />
                    </div>
                    <ArrowForwardIos className={styles.ArrowIcon} onClick={handleNextDate} />
                </div>
            </div>
            {loadingMealDetail && <Loading overall />}
            {isClosingDay ? (
                <div className={styles.MealContainer}>
                    <div className={styles.MealList}>
                        <Title text={t('isClosingDay')} className={styles.Text} />
                    </div>
                </div>
            ) : (
                <>
                    <MealIndicators
                        mealDayDetailByType={mealDayDetailByType}
                        getOrderAdjustment={getOrderAdjustment}
                        onScrollToChildrenWithoutMenu={() =>
                            childrenTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }
                    />
                    <div className={styles.MealContainer}>
                        <div className={styles.TitleAndButton}>
                            <Title text={t('mealDetails')} className={styles.Title} />
                            {canUpdate && <ButtonTextIcon type={'add'} onClick={() => setAddMealDialogOpen(true)} />}
                        </div>
                        {availableMealTypes.length >= 1 && (
                            <div className={styles.ToolbarRow}>
                                <div className={styles.SelectorMealType}>
                                    <SelectMultiple
                                        label={t('mealType')}
                                        selectOptions={mealTypeOptions}
                                        selectValues={selectedMealTypes}
                                        setSelectValues={handleSelectMealTypes}
                                        className={styles.Selector}
                                    />
                                </div>
                            </div>
                        )}
                        <MealDetailedView
                            mealDayDetailByType={mealDayDetailByType}
                            canUpdate={canUpdate}
                            mealTypeTranslations={mealTypeTranslations}
                            getOrderAdjustment={getOrderAdjustment}
                            handleAdjustOrder={handleAdjustOrder}
                            handleFreezeMealType={handleFreezeMealType}
                        />
                    </div>
                    {(globalChilds.length > 0 || childsWithoutMenu.length > 0) && (
                        <MealChildrenTable
                            childs={globalChilds}
                            childsWithoutMenu={childsWithoutMenu}
                            selectedSectionCount={selectedSectionAndSectionGroup.length}
                            isRpeOrLaep={isRpeOrLaep}
                            containerRef={childrenTableRef}
                        />
                    )}
                    {isDatePickerDialogOpen && (
                        <DatePickerDialog
                            open={isDatePickerDialogOpen}
                            handleClose={() => setDatePickerDialogOpen(false)}
                            selectedDate={moment(date, 'DD-MM-YYYY')}
                            handleDateChange={setDateValues}
                            currentDaycare={currentDaycare}
                        />
                    )}
                    {isAddMealDialogOpen && (
                        <UpdateMealQuantityDialog
                            open={isAddMealDialogOpen}
                            handleClose={() => setAddMealDialogOpen(false)}
                            refetch={refetchMealDetail}
                            daycareId={currentDaycare.id}
                            date={date}
                            menus={availableMenus}
                            textures={availableTextures}
                            habits={availableHabits}
                            sections={selectedSections}
                            isRpeOrLaep={isRpeOrLaep}
                            selectedMealTypes={selectedMealTypes}
                            mealAccessibilitySections={mealAccessibilitySections}
                        />
                    )}
                    {isExportDialogOpen && (
                        <ExportMealDialog
                            open={isExportDialogOpen}
                            handleClose={() => setExportDialogOpen(false)}
                            daycareId={currentDaycare.id}
                            date={date}
                            sections={uniqueSections}
                            preSelectedSectionIds={getSectionIdsFromSelection(selectedSectionAndSectionGroup)}
                            isRpeOrLaep={isRpeOrLaep}
                            availableMealTypes={availableMealTypes}
                            mealTypeTranslations={mealTypeTranslations}
                            preSelectedMealTypes={selectedMealTypes}
                        />
                    )}
                </>
            )}
        </>
    ) : (
        <>
            {user && user.daycares.length > 1 && (
                <div className={styles.SelectorContainer}>
                    <div className={styles.SelectorLeft}>
                        <DaycareSelector />
                    </div>
                </div>
            )}
            <div className={styles.MealContainer}>
                <div className={styles.NoMealAccessibility}>
                    <Title text={t('noMealAccessibilityConfigured')} className={styles.Title} />
                    <p>{t('configureMealAccessibilityInSettings')}</p>
                    <ButtonTextIcon
                        type={'settings'}
                        className={styles.Button}
                        onClick={() => navigate(`/daycare?daycare=${currentDaycare.id}#meal`)}
                    />
                </div>
            </div>
        </>
    )
}

export default MealHandler
